/**
 * React Query hook that fetches the current user from `/api/v1/core/me` on the
 * shared Ampersand API platform. Copied from the cockpit UI's
 * lib/useSession.ts: same API platform, same session cookie, same auth flow
 * (Microsoft Entra for @ampersandbrands.com, Google Workspace for
 * @lolliandpops.com).
 *
 * - `user`: the SessionUser object, or null if not authenticated
 * - `isLoading`: initial fetch in flight
 * - `isError`: network or 5xx error (401 is a normal "not logged in" state)
 * - `isAuthenticated`: shortcut for `user != null`
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet, type ApiError } from './api';
import { API_BASE } from './apiBase';

// GET /v1/core/me returns the principal directly:
// { type, id, email, org_scope, permissions, super_admin }.
//
// `super_admin` (spec 00_access-binary-grants.md, 2026-08-09) is a flag from
// its own DB table, not a permission string: a super-admin bypasses every
// per-tool accessSlug check. Mirrors cockpit's `ui/src/lib/useSession.ts`.
export type SessionUser = {
  type: string;
  id: string;
  email: string;
  name?: string;
  org_scope: string[];
  permissions: string[];
  superAdmin: boolean;
};

type Principal = {
  type: string;
  id: string;
  email: string;
  name?: string;
  org_scope?: string[];
  permissions?: string[];
  super_admin?: boolean;
};

export async function fetchMe(): Promise<SessionUser | null> {
  try {
    const p = await apiGet<Principal>(`${API_BASE}/v1/core/me`);
    return {
      type: p.type,
      id: p.id,
      email: p.email,
      name: p.name,
      org_scope: p.org_scope ?? [],
      permissions: p.permissions ?? [],
      superAdmin: p.super_admin ?? false,
    };
  } catch (err) {
    const apiErr = err as ApiError;
    // 401: no valid session — redirect to login.
    // 403: authenticated but missing core:read — do NOT redirect (causes an
    //      infinite loop); let it propagate so the caller can show an
    //      access-denied page instead.
    if (apiErr.status === 401) {
      return null;
    }
    throw err;
  }
}

export type UseSessionResult = {
  user: SessionUser | null;
  isLoading: boolean;
  isError: boolean;
  isAuthenticated: boolean;
  /** True when /v1/core/me returned 403: user is authenticated but lacks core:read. */
  isForbidden: boolean;
};

export function useSession(): UseSessionResult {
  const query = useQuery<SessionUser | null, Error>({
    queryKey: ['session'],
    queryFn: fetchMe,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const user = query.data ?? null;
  const isForbidden = (query.error as ApiError)?.status === 403;
  return {
    user,
    isLoading: query.isLoading,
    isError: query.isError,
    isAuthenticated: user != null,
    isForbidden,
  };
}

export function getLoginUrl(returnTo?: string): string {
  // Absolute return URL: the api's post-login redirect defaults to cockpit's
  // frontend base for path-style returns, so this app must send its own origin
  // (allowed server-side via AUTH_RETURN_ORIGINS).
  const path = returnTo ?? `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams({ return: `${window.location.origin}${path}` });
  return `${API_BASE}/v1/core/auth/login?${params.toString()}`;
}

// Microsoft Entra login (Store Support Center / HQ staff). Alias of
// getLoginUrl, named so the login page reads clearly next to the Google path.
export function getEntraLoginUrl(returnTo?: string): string {
  return getLoginUrl(returnTo);
}

// Google Workspace login (Lolli & Pops store staff). Kicks off the OIDC flow
// on the API; `login_hint` pre-fills Google's account chooser with the email
// the user typed.
export function getGoogleLoginUrl(email?: string, returnTo?: string): string {
  const path = returnTo ?? `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams({ return: `${window.location.origin}${path}` });
  if (email) params.set('login_hint', email);
  return `${API_BASE}/v1/core/auth/google/login?${params.toString()}`;
}

// The email-domain-to-provider routing rule, shared by the login page.
// @ampersandbrands.com -> Microsoft Entra; @lolliandpops.com -> Google.
export type LoginProvider = 'entra' | 'google' | 'unknown';

export function providerForEmail(email: string): LoginProvider {
  const at = email.lastIndexOf('@');
  if (at < 0) return 'unknown';
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (domain === 'ampersandbrands.com') return 'entra';
  if (domain === 'lolliandpops.com') return 'google';
  return 'unknown';
}

// `everywhere=true` triggers the full Microsoft sign-out (tenant-wide
// Entra end-session). Default is local-only logout: clears the app's cookie
// but leaves Outlook / Teams / SharePoint sessions intact.
export function getLogoutUrl(everywhere = false): string {
  // Send this app's own /login as the post-logout return so the API lands the
  // user back on the Sweet Spot login (allowlisted via AUTH_RETURN_ORIGINS),
  // not the primary frontend. Mirrors the return handling in getLoginUrl.
  const params = new URLSearchParams({ return: `${window.location.origin}/login` });
  if (everywhere) params.set('everywhere', '1');
  return `${API_BASE}/v1/core/auth/logout?${params.toString()}`;
}
